import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../store/root-reducer';
import {
  getEnrichmentJobsApi,
  getEnrichmentStatsApi,
  createEnrichmentJobApi,
  deleteEnrichmentJobApi,
  getEnrichmentJobStatusApi,
  getEnrichmentJobRecordsApi,
} from './helpers/csv-enrichment.api';
import {
  EnrichmentStatus,
  EnrichmentType,
  DateRangeFilter,
} from './enums/csv-enrichment.enums';
import { SortOrder } from '../../shared/enums/order';
import {
  CreateEnrichmentPayload,
  EnrichedRecord,
  EnrichmentJob,
  EnrichmentStats,
} from './types/csv-enrichment.types';
import { ResponseErrorWithHandled } from '../../shared/types';

export const getEnrichmentJobs = createAsyncThunk<
  { jobs: EnrichmentJob[]; total: number },
  {
    dateRange: DateRangeFilter;
    search: string;
    status: EnrichmentStatus | null;
    type: EnrichmentType | null;
    page: number;
    pageSize: number;
    sortBy: string | null;
    sortOrder: SortOrder | null;
  }
>('csvEnrichment/getEnrichmentJobs', async (filters, thunkAPI) => {
  try {
    const response = await getEnrichmentJobsApi(filters);
    return (response as any)?.payload || { jobs: [], total: 0 };
  } catch (error) {
    return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
  }
});

export const getEnrichmentStats = createAsyncThunk<EnrichmentStats>(
  'csvEnrichment/getEnrichmentStats',
  async (_, thunkAPI) => {
    try {
      const response = await getEnrichmentStatsApi();
      return (
        (response as any)?.payload || {
          totalFiles: 0,
          peopleEnriched: 0,
          companyEnriched: 0,
        }
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
    }
  },
);

export const createEnrichmentJob = createAsyncThunk<
  EnrichmentJob & { shouldPoll?: boolean },
  CreateEnrichmentPayload
>('csvEnrichment/createEnrichmentJob', async (payload, thunkAPI) => {
  try {
    const response = await createEnrichmentJobApi(payload);
    return (response as any)?.payload;
  } catch (error) {
    return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
  }
});

export const deleteEnrichmentJob = createAsyncThunk<
  { success: boolean; id: string; message?: string },
  string
>('csvEnrichment/deleteEnrichmentJob', async (id, thunkAPI) => {
  try {
    const response = await deleteEnrichmentJobApi(id);
    const message =
      (response as any)?.message ?? (response as any)?.payload?.message;
    return { success: true, id, message };
  } catch (error) {
    return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
  }
});

export const getEnrichmentJobStatus = createAsyncThunk<
  {
    id: string;
    status: EnrichmentStatus;
    progress: number;
    enrichedRecords: number;
    matchPercentage: number;
  },
  string
>('csvEnrichment/getEnrichmentJobStatus', async (id, thunkAPI) => {
  try {
    const response = await getEnrichmentJobStatusApi(id);
    return (
      (response as any)?.payload || {
        id,
        status: EnrichmentStatus.FAILED,
        progress: 0,
        enrichedRecords: 0,
        matchPercentage: 0,
      }
    );
  } catch (error) {
    return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
  }
});

export const ENRICHMENT_RECORDS_PAGE_SIZE = 25;

export type GetEnrichmentJobRecordsArg =
  | string
  | { jobId: string; preserveAccumulatedPages?: boolean };

export const getEnrichmentJobRecords = createAsyncThunk<
  {
    records: EnrichedRecord[];
    columns: string[];
    total: number;
    job: EnrichmentJob | null;
    preserveAccumulatedPages: boolean;
  },
  GetEnrichmentJobRecordsArg
>('csvEnrichment/getEnrichmentJobRecords', async (arg, thunkAPI) => {
  const jobId = typeof arg === 'string' ? arg : arg.jobId;
  const preserveAccumulatedPages =
    typeof arg === 'object' && Boolean(arg.preserveAccumulatedPages);

  try {
    const response = await getEnrichmentJobRecordsApi(
      jobId,
      1,
      ENRICHMENT_RECORDS_PAGE_SIZE,
    );
    const payload = (response as any)?.payload;
    const base = payload
      ? {
          records: payload.records ?? [],
          columns: payload.columns ?? [],
          total: payload.total ?? 0,
          job: payload.job ?? null,
        }
      : { records: [], columns: [], total: 0, job: null };
    return { ...base, preserveAccumulatedPages };
  } catch (error) {
    return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
  }
});

export const loadMoreEnrichmentJobRecords = createAsyncThunk<
  {
    records: EnrichedRecord[];
    columns: string[];
    total: number;
    job: EnrichmentJob | null;
  },
  string
>('csvEnrichment/loadMoreEnrichmentJobRecords', async (id, thunkAPI) => {
  const state = thunkAPI.getState() as RootState;
  const nextPage = state.csvEnrichment.recordsCurrentPage + 1;
  const availableRecords =
    state.csvEnrichment.recordsJob?.enrichedRecords ??
    state.csvEnrichment.recordsJob?.totalRecords ??
    0;

  if (state.csvEnrichment.records.length >= availableRecords) {
    return { records: [], columns: [], total: availableRecords, job: null };
  }

  try {
    const response = await getEnrichmentJobRecordsApi(
      id,
      nextPage,
      ENRICHMENT_RECORDS_PAGE_SIZE,
    );
    const payload = (response as any)?.payload;
    return payload
      ? {
          records: payload.records ?? [],
          columns: payload.columns ?? [],
          total: payload.total ?? 0,
          job: payload.job ?? null,
        }
      : { records: [], columns: [], total: 0, job: null };
  } catch (error) {
    return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
  }
});
